import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

const colors = {
  gold: '#C5A572',
  burgundy: '#800020',
  navy: '#1A1F2E',
  cream: '#FDFAF3',
  charcoal: '#2C2C2C',
};

const ReceiptContainer = styled(Paper)({
  padding: '24px',
  maxWidth: '400px',
  margin: '0 auto',
  backgroundColor: '#FFFFFF',
  border: `2px solid ${colors.gold}`,
  borderRadius: '16px',
  boxShadow: `0 10px 30px -10px ${alpha(colors.gold, 0.3)}`,
  fontFamily: '"Times New Roman", serif',
});

const HeaderBox = styled(Box)({
  textAlign: 'center',
  marginBottom: '20px',
  '& h4': {
    color: colors.navy,
    fontWeight: 700,
    margin: 0,
  },
  '& p': {
    color: colors.burgundy,
    margin: '4px 0',
  },
});

const DividerStyled = styled(Divider)({
  margin: '16px 0',
  borderColor: colors.gold,
  borderWidth: '1px',
  opacity: 0.5,
});

interface ReceiptProps {
  registration: any; // Define proper type based on your registration data
}

export const Receipt: React.FC<ReceiptProps> = ({ registration }) => {
  if (!registration) return null;

  const { registrationNo, patient, tests, totalAmount, discountAmount, paidAmount, createdAt } = registration;

  return (
    <ReceiptContainer>
      <HeaderBox>
        <Typography variant="h4">Elite Diagnostics</Typography>
        <Typography variant="subtitle2">Corporate Health Partner</Typography>
        <Typography variant="body2">123 Luxury Avenue, Kanchipuram</Typography>
        <Typography variant="body2">GST: 33ABCDE1234F1Z5</Typography>
      </HeaderBox>

      <DividerStyled />

      <Box mb={2}>
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Receipt No:</span> <strong>{registrationNo}</strong>
        </Typography>
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Date:</span> <strong>{new Date(createdAt).toLocaleDateString()}</strong>
        </Typography>
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Patient:</span> <strong>{patient?.name || 'N/A'}</strong>
        </Typography>
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Mobile:</span> <strong>{patient?.mobile || 'N/A'}</strong>
        </Typography>
      </Box>

      <DividerStyled />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: colors.navy }}>Test Name</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: colors.navy }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tests && tests.map((test: any, index: number) => (
              <TableRow key={index}>
                <TableCell>{test.name || test.TestName}</TableCell>
                <TableCell align="right">₹{test.amount || test.Amount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DividerStyled />

      <Box>
        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Subtotal:</span> <span>₹{totalAmount}</span>
        </Typography>
        {discountAmount > 0 && (
          <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', color: colors.burgundy }}>
            <span>Discount:</span> <span>- ₹{discountAmount}</span>
          </Typography>
        )}
        <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, color: colors.navy }}>
          <span>Total:</span> <span>₹{paidAmount || totalAmount - discountAmount}</span>
        </Typography>
      </Box>

      <DividerStyled />

      <Box textAlign="center" mt={2}>
        <Typography variant="body2" sx={{ color: colors.burgundy, fontStyle: 'italic' }}>
          Thank you for choosing Elite Diagnostics
        </Typography>
        <Typography variant="caption" sx={{ color: colors.gold }}>
          * This is a computer generated receipt *
        </Typography>
      </Box>
    </ReceiptContainer>
  );
};

// import React from 'react';
// import {
//   Paper,
//   Typography,
//   Box,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Divider,
// } from '@mui/material';
// import { format } from 'date-fns';
// import { Registration } from '../../types/registration.types';

// interface ReceiptProps {
//   registration: Registration;
// }

// export const Receipt = React.forwardRef<HTMLDivElement, ReceiptProps>(
//   ({ registration }, ref) => {
//     return (
//       <Paper 
//         ref={ref} 
//         elevation={3} 
//         sx={{ 
//           p: 3, 
//           maxWidth: 400, 
//           mx: 'auto',
//           fontFamily: 'monospace',
//         }}
//       >
//         <Typography variant="h6" align="center" gutterBottom>
//           🏥 DIAGNOSTIC LAB
//         </Typography>
//         <Typography variant="subtitle2" align="center" color="textSecondary">
//           {format(new Date(registration.RegisteredOn), 'dd/MM/yyyy HH:mm')}
//         </Typography>
        
//         <Divider sx={{ my: 2 }} />
        
//         <Typography variant="body2" align="center" gutterBottom>
//           Receipt #{registration.RegistrationNumber}
//         </Typography>
        
//         <Divider sx={{ my: 2 }} />
        
//         <Box mb={2}>
//           <Typography variant="body2">
//             <strong>Patient:</strong> {registration.Customer?.CustomerName}
//           </Typography>
//           <Typography variant="body2">
//             <strong>Age/Gender:</strong> {registration.Customer?.Age || 'N/A'}/{registration.Customer?.Gender || 'N/A'}
//           </Typography>
//           <Typography variant="body2">
//             <strong>Mobile:</strong> {registration.Customer?.MobileNumber || 'N/A'}
//           </Typography>
//         </Box>

//         <TableContainer>
//           <Table size="small">
//             <TableHead>
//               <TableRow>
//                 <TableCell>Test</TableCell>
//                 <TableCell align="right">Amount</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {registration.tests?.map((test) => (
//                 <TableRow key={test.RegistrationTestId}>
//                   <TableCell>
//                     {test.TestName}
//                   </TableCell>
//                   <TableCell align="right">₹{test.TestAmount || 0}</TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>

//         <Divider sx={{ my: 2 }} />

//         <Box>
//           <Box display="flex" justifyContent="space-between">
//             <Typography variant="body2">Sub Total:</Typography>
//             <Typography variant="body2">₹{registration.TotalAmount || 0}</Typography>
//           </Box>
//           <Box display="flex" justifyContent="space-between" mt={1}>
//             <Typography variant="subtitle1">Net Total:</Typography>
//             <Typography variant="subtitle1" color="primary">
//               ₹{registration.PaidAmount || 0}
//             </Typography>
//           </Box>
//         </Box>

//         <Divider sx={{ my: 2 }} />

//         <Box>
//           <Typography variant="body2">
//             <strong>Status:</strong> {registration.Full_Paid_Flag ? 'Paid' : 'Partial'}
//           </Typography>
//         </Box>

//         <Box mt={2} textAlign="center">
//           <Typography variant="caption" color="textSecondary">
//             Thank you for choosing us!
//           </Typography>
//           <Typography variant="caption" display="block" color="textSecondary">
//             Reports will be sent via WhatsApp/Email
//           </Typography>
//         </Box>
//       </Paper>
//     );
//   }
// );

// import React from 'react';
// import {
//   Paper,
//   Typography,
//   Box,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Divider,
// } from '@mui/material';
// import { format } from 'date-fns';

// interface ReceiptProps {
//   registration: any;
// }

// export const Receipt = React.forwardRef<HTMLDivElement, ReceiptProps>(
//   ({ registration }, ref) => {
//     return (
//       <Paper 
//         ref={ref} 
//         elevation={3} 
//         sx={{ 
//           p: 3, 
//           maxWidth: 400, 
//           mx: 'auto',
//           fontFamily: 'monospace',
//         }}
//       >
//         <Typography variant="h6" align="center" gutterBottom>
//           🏥 DIAGNOSTIC LAB
//         </Typography>
//         <Typography variant="subtitle2" align="center" color="textSecondary">
//           {format(new Date(), 'dd/MM/yyyy HH:mm')}
//         </Typography>
        
//         <Divider sx={{ my: 2 }} />
        
//         <Typography variant="body2" align="center" gutterBottom>
//           Receipt #{registration.registrationNo}
//         </Typography>
        
//         <Divider sx={{ my: 2 }} />
        
//         <Box mb={2}>
//           <Typography variant="body2">
//             <strong>Patient:</strong> {registration.patient.name}
//           </Typography>
//           <Typography variant="body2">
//             <strong>Age/Gender:</strong> {registration.patient.age}/{registration.patient.gender}
//           </Typography>
//           <Typography variant="body2">
//             <strong>Mobile:</strong> {registration.patient.mobile}
//           </Typography>
//         </Box>

//         <TableContainer>
//           <Table size="small">
//             <TableHead>
//               <TableRow>
//                 <TableCell>Test</TableCell>
//                 <TableCell align="right">Amount</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {registration.tests.map((test: any) => (
//                 <TableRow key={test.id}>
//                   <TableCell>
//                     {test.testName}
//                     <Typography variant="caption" display="block" color="textSecondary">
//                       {test.testCode}
//                     </Typography>
//                   </TableCell>
//                   <TableCell align="right">₹{test.price}</TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>

//         <Divider sx={{ my: 2 }} />

//         <Box>
//           <Box display="flex" justifyContent="space-between">
//             <Typography variant="body2">Sub Total:</Typography>
//             <Typography variant="body2">₹{registration.totalAmount}</Typography>
//           </Box>
//           <Box display="flex" justifyContent="space-between">
//             <Typography variant="body2">Discount:</Typography>
//             <Typography variant="body2" color="error">-₹{registration.discountAmount}</Typography>
//           </Box>
//           <Box display="flex" justifyContent="space-between" mt={1}>
//             <Typography variant="subtitle1">Net Total:</Typography>
//             <Typography variant="subtitle1" color="primary">
//               ₹{registration.netAmount}
//             </Typography>
//           </Box>
//         </Box>

//         <Divider sx={{ my: 2 }} />

//         <Box>
//           <Typography variant="body2">
//             <strong>Payment:</strong> {registration.paymentMethod}
//           </Typography>
//           <Typography variant="body2">
//             <strong>Status:</strong> {registration.paymentStatus}
//           </Typography>
//         </Box>

//         <Box mt={2} textAlign="center">
//           <Typography variant="caption" color="textSecondary">
//             Thank you for choosing us!
//           </Typography>
//           <Typography variant="caption" display="block" color="textSecondary">
//             Reports will be sent via WhatsApp/Email
//           </Typography>
//         </Box>
//       </Paper>
//     );
//   }
// );